/* global chrome */
// Ponte com a extensão de passkey (WebAuthn) usada para concluir a conexão de
// sessões WhatsApp/WAHA que exigem passkey. A extensão abre uma aba em
// web.whatsapp.com e assina o desafio ali (o navegador só permite a assinatura
// naquela origem). Ver browser-extension/ para o código da extensão.

// IDs aceitos no Chrome: a extensão publicada pela WAHA (funciona hoje) + a
// nossa (ChatSuite) assim que for publicada — é só acrescentar o ID aqui.
export const CHATSUITE_EXTENSION_IDS = ['kihcbnjelmhgljhjdahofddiiijlgdjn'];

// Links de instalação mostrados ao operador quando a extensão não é detectada.
export const EXTENSION_INSTALL_URL = {
  chrome:
    'https://chromewebstore.google.com/detail/kihcbnjelmhgljhjdahofddiiijlgdjn',
  firefox: 'https://addons.mozilla.org/firefox/addon/waha-conectar-sessao/',
};

export const isChrome = () =>
  typeof chrome !== 'undefined' && !!chrome?.runtime?.sendMessage;

const pingChromeId = (id, timeoutMs = 300) =>
  new Promise(resolve => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    try {
      chrome.runtime.sendMessage(id, { type: 'waha-passkey-ping' }, resp => {
        clearTimeout(timer);
        resolve(!chrome.runtime.lastError && resp?.ok === true);
      });
    } catch {
      clearTimeout(timer);
      resolve(false);
    }
  });

// Retorna o ID da 1ª extensão Chrome que responder ao ping, ou null.
const detectChromeExtensionId = async () => {
  if (!isChrome()) return null;
  // eslint-disable-next-line no-restricted-syntax
  for (const id of CHATSUITE_EXTENSION_IDS) {
    // eslint-disable-next-line no-await-in-loop
    if (await pingChromeId(id)) return id;
  }
  return null;
};

const pingFirefoxExtension = (timeoutMs = 300) =>
  new Promise(resolve => {
    const requestId = crypto.randomUUID();
    const controller = new AbortController();
    let timer;
    const done = ok => {
      clearTimeout(timer);
      controller.abort();
      resolve(ok);
    };
    const onMessage = event => {
      if (event.source !== window) return;
      const data = event.data;
      if (
        data?.source !== 'waha-passkey-extension' ||
        data.requestId !== requestId
      ) {
        return;
      }
      done(!!data.ok);
    };
    timer = setTimeout(() => done(false), timeoutMs);
    window.addEventListener('message', onMessage, {
      signal: controller.signal,
    });
    window.postMessage(
      { source: 'waha-passkey-page', requestId, type: 'waha-passkey-ping' },
      window.location.origin
    );
  });

// Detecta a extensão. Retorna { available, browser, extensionId }.
export const detectPasskeyExtension = async () => {
  if (isChrome()) {
    const extensionId = await detectChromeExtensionId();
    return { available: !!extensionId, browser: 'chrome', extensionId };
  }
  const available = await pingFirefoxExtension();
  return { available, browser: 'firefox', extensionId: null };
};

const signWithChrome = (extensionId, challenge) =>
  new Promise((resolve, reject) => {
    chrome.runtime.sendMessage(
      extensionId,
      { type: 'waha-passkey-sign', challenge },
      resp => {
        if (chrome.runtime.lastError)
          return reject(new Error(chrome.runtime.lastError.message));
        if (!resp?.ok)
          return reject(new Error(resp?.error || 'falha ao assinar a passkey'));
        return resolve(resp.assertion);
      }
    );
  });

const signWithFirefox = (challenge, timeoutMs = 120000) =>
  new Promise((resolve, reject) => {
    const requestId = crypto.randomUUID();
    const controller = new AbortController();
    let timer;
    const finish = fn => {
      clearTimeout(timer);
      controller.abort();
      fn();
    };
    const onMessage = event => {
      if (event.source !== window) return;
      const data = event.data;
      if (
        data?.source !== 'waha-passkey-extension' ||
        data.requestId !== requestId
      ) {
        return;
      }
      if (!data.ok) {
        finish(() => reject(new Error(data.error || 'falhou')));
        return;
      }
      finish(() => resolve(data.assertion));
    };
    timer = setTimeout(
      () => finish(() => reject(new Error('timeout ao assinar a passkey'))),
      timeoutMs
    );
    window.addEventListener('message', onMessage, {
      signal: controller.signal,
    });
    window.postMessage(
      {
        source: 'waha-passkey-page',
        requestId,
        type: 'waha-passkey-sign',
        challenge,
      },
      window.location.origin
    );
  });

// Assina o desafio via extensão. `detection` = retorno de detectPasskeyExtension().
export const signPasskeyChallenge = (detection, challenge) => {
  if (detection.browser === 'chrome') {
    return signWithChrome(detection.extensionId, challenge);
  }
  return signWithFirefox(challenge);
};
